import { useId, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  Shield,
  ShieldCheck,
  ListChecks,
  Search,
  ChevronDown,
  Sparkles,
  Layers,
  CheckCircle2,
  Crown,
  Briefcase,
  User as UserIcon,
  Eye,
  Calculator,
  HeartHandshake,
  Star,
} from 'lucide-react'
import { PageShell } from '@/components/common/PageShell'
import { Button, Card, Input, Spinner } from '@/components/ui'
import { createRole } from '@/api/services/roles.service'
import type {
  CreateRolePayload,
  Permission,
  PermissionBundle,
  RoleTemplate,
  RoleTemplateSlug,
} from '@/types'
import { useAllPermissions, useRoleTemplates, rolesQueryKeys } from '../hooks/useRoles'
import { getErrorMessage } from '@/lib/getErrorMessage'
import { cn } from '@/lib/cn'

const createRoleSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  slug: z.string().optional(),
  description: z.string().optional(),
  selectedPermissionIds: z.array(z.string()),
})

type CreateRoleFormValues = z.infer<typeof createRoleSchema>

const TEMPLATE_META: Record<
  RoleTemplateSlug,
  { icon: React.ComponentType<{ className?: string }>; tagline: string }
> = {
  admin: { icon: Crown, tagline: 'Full organization-wide access' },
  auditor: { icon: Eye, tagline: 'Read-only oversight across the org' },
  accountant: { icon: Calculator, tagline: 'Finance: payroll, tax, bonuses across companies' },
  management: { icon: Briefcase, tagline: 'Manage a company’s team + own employee data' },
  hr: { icon: HeartHandshake, tagline: 'People ops: leave, onboarding, documents' },
  'team-lead': { icon: Star, tagline: 'Senior staff with team approval rights' },
  staff: { icon: UserIcon, tagline: 'Own data only' },
}

function scopeBadgeText(scope: 'all' | 'company' | 'self'): string {
  if (scope === 'all') return 'Org-wide'
  if (scope === 'company') return 'Company-scoped'
  return 'Personal'
}

function slugify(input: string): string {
  return input
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
}

function selectedIdsToBundles(
  selectedIds: string[],
  allPermissions: Permission[]
): PermissionBundle[] {
  const byModule = new Map<string, string[]>()
  for (const id of selectedIds) {
    const p = allPermissions.find((x) => x._id === id)
    if (!p) continue
    const actions = byModule.get(p.module) ?? []
    if (!actions.includes(p.action)) actions.push(p.action)
    byModule.set(p.module, actions)
  }
  return Array.from(byModule.entries()).map(([module, actions]) => ({ module, actions }))
}

function formValuesToPayload(
  values: CreateRoleFormValues,
  allPermissions: Permission[],
  templateSlug: RoleTemplateSlug | null
): CreateRolePayload {
  const slug = values.slug?.trim()
  return {
    name: values.name.trim(),
    slug: slug || undefined,
    description: values.description?.trim() || undefined,
    permissions: selectedIdsToBundles(values.selectedPermissionIds, allPermissions),
    // Scope and hierarchy are derived: from the picked template, or backend defaults
    // (scope='self', hierarchyLevel=1) when no template was chosen.
    templateSlug: templateSlug ?? undefined,
  }
}

function bundlesToSelectedIds(
  bundles: PermissionBundle[],
  allPermissions: Permission[]
): string[] {
  const ids: string[] = []
  const moduleActions = new Map<string, Set<string>>()
  for (const b of bundles) {
    if (!moduleActions.has(b.module)) moduleActions.set(b.module, new Set())
    for (const a of b.actions) moduleActions.get(b.module)!.add(a)
  }
  for (const p of allPermissions) {
    if (moduleActions.get(p.module)?.has(p.action)) ids.push(p._id)
  }
  return ids
}

function permissionLabel(p: Permission): string {
  if (p.code) return p.code.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  return `${p.module} ${p.action}`.replace(/\b\w/g, (c) => c.toUpperCase())
}

export function CreateRolePage(): React.ReactElement {
  const navigate = useNavigate()
  const formId = useId()
  const queryClient = useQueryClient()
  const { data: allPermissions = [], isLoading: permissionsLoading } = useAllPermissions()
  const { data: templates = [], isLoading: templatesLoading } = useRoleTemplates()

  const [search, setSearch] = useState('')
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(new Set())
  const [selectedTemplate, setSelectedTemplate] = useState<RoleTemplateSlug | null>(null)

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<CreateRoleFormValues>({
    resolver: zodResolver(createRoleSchema),
    defaultValues: {
      name: '',
      slug: '',
      description: '',
      selectedPermissionIds: [],
    },
  })

  const name = watch('name')
  const slug = watch('slug')
  const selectedPermissionIds = watch('selectedPermissionIds')

  const activeTemplate = useMemo(
    () => templates.find((t) => t.slug === selectedTemplate) ?? null,
    [templates, selectedTemplate]
  )
  const effectiveScope = activeTemplate?.scope ?? 'self'

  const applyTemplate = (template: RoleTemplate): void => {
    setSelectedTemplate(template.slug)
    if (!getValues('description')?.trim()) {
      setValue('description', template.description, { shouldDirty: true })
    }
    if (allPermissions.length > 0) {
      setValue(
        'selectedPermissionIds',
        bundlesToSelectedIds(template.permissions, allPermissions),
        { shouldDirty: true }
      )
    }
  }

  const mutation = useMutation({
    mutationFn: (payload: CreateRolePayload) => createRole(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: rolesQueryKeys.all })
      toast.success('Role created')
      navigate('/roles')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const groupedPermissions = useMemo(() => {
    const byCategory = new Map<string, Permission[]>()
    for (const p of allPermissions) {
      const key = (p.category ?? p.module ?? 'Other').toUpperCase()
      if (!byCategory.has(key)) byCategory.set(key, [])
      byCategory.get(key)!.push(p)
    }
    return Array.from(byCategory.entries()).sort(([a], [b]) => a.localeCompare(b))
  }, [allPermissions])

  const filteredGroupedPermissions = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return groupedPermissions
    return groupedPermissions
      .map(([category, perms]) => {
        const matched = perms.filter((p) => {
          return (
            p.code?.toLowerCase().includes(q) ||
            p.description?.toLowerCase().includes(q) ||
            p.module?.toLowerCase().includes(q) ||
            p.action?.toLowerCase().includes(q)
          )
        })
        return [category, matched] as const
      })
      .filter(([, perms]) => perms.length > 0)
  }, [groupedPermissions, search])

  const togglePermission = (permissionId: string): void => {
    const current = getValues('selectedPermissionIds')
    const next = current.includes(permissionId)
      ? current.filter((id) => id !== permissionId)
      : [...current, permissionId]
    setValue('selectedPermissionIds', next, { shouldDirty: true })
  }

  const selectAllInCategory = (perms: Permission[]): void => {
    const current = getValues('selectedPermissionIds')
    const ids = perms.map((p) => p._id)
    const allSelected = ids.every((id) => current.includes(id))
    const next = allSelected
      ? current.filter((id) => !ids.includes(id))
      : Array.from(new Set([...current, ...ids]))
    setValue('selectedPermissionIds', next, { shouldDirty: true })
  }

  const toggleCategoryCollapsed = (category: string): void => {
    setCollapsedCategories((prev) => {
      const next = new Set(prev)
      if (next.has(category)) next.delete(category)
      else next.add(category)
      return next
    })
  }

  const handleNameBlur = (): void => {
    if (!getValues('slug')?.trim() && name.trim()) {
      setValue('slug', slugify(name), { shouldDirty: true })
    }
  }

  const onSubmit = (values: CreateRoleFormValues): void => {
    if (!selectedTemplate) {
      toast.error('Pick a template to start')
      return
    }
    mutation.mutate(formValuesToPayload(values, allPermissions, selectedTemplate))
  }

  const totalPermissions = allPermissions.length
  const selectedCount = selectedPermissionIds.length
  const selectedByCategory = useMemo(() => {
    const map = new Map<string, number>()
    for (const [category, perms] of groupedPermissions) {
      const count = perms.filter((p) => selectedPermissionIds.includes(p._id)).length
      if (count > 0) map.set(category, count)
    }
    return map
  }, [groupedPermissions, selectedPermissionIds])

  const saving = mutation.isPending

  return (
    <PageShell
      breadcrumbItems={[
        { label: 'Organization', to: '/dashboard' },
        { label: 'Roles', to: '/roles' },
        { label: 'New Role' },
      ]}
      icon={Shield}
      title="Create New Role"
      description="Define a role and choose the permissions members will inherit."
      actions={
        <Button type="button" variant="ghost" onClick={() => navigate('/roles')}>
          Cancel
        </Button>
      }
    >
      <form
        id={formId}
        onSubmit={handleSubmit(onSubmit)}
        className="grid grid-cols-1 gap-6 pb-24 lg:grid-cols-3 lg:pb-0"
      >
        {/* LEFT COLUMN */}
        <div className="space-y-6 lg:col-span-2">
          {/* Template picker */}
          <Card
            header={
              <div className="flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <Sparkles className="h-5 w-5" aria-hidden />
                </span>
                <div>
                  <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    Pick a role template <span className="text-danger">*</span>
                  </h2>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Required. Determines scope and seeds permissions. You can adjust permissions afterward.
                  </p>
                </div>
              </div>
            }
            contentClassName="p-6"
          >
            {templatesLoading ? (
              <div className="flex items-center gap-2">
                <Spinner size="sm" />
                <span className="text-sm text-gray-600 dark:text-gray-400">Loading templates…</span>
              </div>
            ) : (
              <div className="space-y-5">
                {(
                  [
                    { scope: 'all' as const, label: 'Org-wide', hint: 'Operates across the whole organization' },
                    { scope: 'company' as const, label: 'Company-scoped', hint: 'Limited to companies the user belongs to' },
                    { scope: 'self' as const, label: 'Personal', hint: 'Sees only their own data' },
                  ]
                ).map((tier) => {
                  const tierTemplates = templates.filter((t) => t.scope === tier.scope)
                  if (tierTemplates.length === 0) return null
                  return (
                    <div key={tier.scope}>
                      <div className="mb-2 flex items-baseline justify-between">
                        <h3 className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                          {tier.label}
                        </h3>
                        <span className="text-[11px] text-gray-400 dark:text-gray-500">
                          {tier.hint}
                        </span>
                      </div>
                      <div
                        className={cn(
                          'grid gap-3',
                          tierTemplates.length === 1
                            ? 'grid-cols-1'
                            : tierTemplates.length === 2
                              ? 'grid-cols-1 md:grid-cols-2'
                              : 'grid-cols-1 md:grid-cols-3'
                        )}
                      >
                        {tierTemplates.map((tpl) => {
                          const meta = TEMPLATE_META[tpl.slug] ?? TEMPLATE_META.staff
                          const Icon = meta.icon
                          const active = selectedTemplate === tpl.slug
                          return (
                            <button
                              key={tpl.slug}
                              type="button"
                              onClick={() => applyTemplate(tpl)}
                              className={cn(
                                'flex flex-col items-start gap-2 rounded-lg border p-4 text-left transition-colors',
                                active
                                  ? 'border-primary bg-primary/5 ring-2 ring-primary/30 dark:bg-primary/10'
                                  : 'border-gray-200 bg-white hover:border-primary/40 hover:bg-primary/5 dark:border-gray-700 dark:bg-gray-800/40 dark:hover:bg-primary/10'
                              )}
                              aria-pressed={active}
                            >
                              <div className="flex w-full items-center justify-between">
                                <span
                                  className={cn(
                                    'flex h-8 w-8 items-center justify-center rounded-md',
                                    active
                                      ? 'bg-primary text-white'
                                      : 'bg-primary/10 text-primary'
                                  )}
                                >
                                  <Icon className="h-4 w-4" aria-hidden />
                                </span>
                                {active && (
                                  <CheckCircle2 className="h-4 w-4 text-primary" aria-hidden />
                                )}
                              </div>
                              <div>
                                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                  {tpl.name}
                                </p>
                                <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                                  {meta.tagline}
                                </p>
                              </div>
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </Card>

          {/* Role details */}
          <Card
            header={
              <div className="flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <ShieldCheck className="h-5 w-5" aria-hidden />
                </span>
                <div>
                  <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    Role details
                  </h2>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Give the role a clear identity.
                  </p>
                </div>
              </div>
            }
            contentClassName="p-6"
          >
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Input
                label="Name"
                required
                {...register('name', { onBlur: handleNameBlur })}
                error={errors.name?.message}
                placeholder="e.g. Team Lead"
              />
              <Input
                label="Slug"
                {...register('slug')}
                error={errors.slug?.message}
                placeholder="auto-generated from name"
                hint="URL-safe identifier. Leave blank to auto-generate."
              />
            </div>
            <div className="mt-4">
              <label
                htmlFor={`${formId}-description`}
                className="mb-2 block text-xs font-medium text-gray-700 dark:text-gray-300"
              >
                Description
              </label>
              <textarea
                id={`${formId}-description`}
                {...register('description')}
                rows={3}
                className="block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-500"
                placeholder="What is this role for?"
              />
            </div>
          </Card>

          {/* Permissions */}
          <Card
            header={
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
                    <ListChecks className="h-5 w-5" aria-hidden />
                  </span>
                  <div>
                    <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      Permissions
                    </h2>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {selectedCount} of {totalPermissions} selected
                    </p>
                  </div>
                </div>
                {selectedCount > 0 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      setValue('selectedPermissionIds', [], { shouldDirty: true })
                    }
                  >
                    Clear all
                  </Button>
                )}
              </div>
            }
            contentClassName="p-0"
          >
            <div className="border-b border-gray-200 px-4 py-3 dark:border-gray-700">
              <div className="relative">
                <Search
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                  aria-hidden
                />
                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search permissions…"
                  className="block h-9 w-full rounded-md border border-gray-300 bg-white pl-9 pr-3 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-500"
                />
              </div>
            </div>

            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {permissionsLoading ? (
                <div className="flex items-center gap-2 px-4 py-8">
                  <Spinner size="sm" />
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    Loading permissions…
                  </span>
                </div>
              ) : filteredGroupedPermissions.length === 0 ? (
                <p className="px-4 py-10 text-center text-sm text-gray-500 dark:text-gray-400">
                  {search
                    ? 'No permissions match your search.'
                    : 'No permissions available. Configure permissions in the system first.'}
                </p>
              ) : (
                filteredGroupedPermissions.map(([category, perms]) => {
                  const collapsed = collapsedCategories.has(category)
                  const categoryIds = perms.map((p) => p._id)
                  const allSelected =
                    categoryIds.length > 0 &&
                    categoryIds.every((id) => selectedPermissionIds.includes(id))
                  const categorySelected = perms.filter((p) =>
                    selectedPermissionIds.includes(p._id)
                  ).length
                  return (
                    <div key={category}>
                      <div className="flex items-center justify-between gap-2 bg-gray-50/70 px-4 py-2.5 dark:bg-gray-800/40">
                        <button
                          type="button"
                          onClick={() => toggleCategoryCollapsed(category)}
                          className="flex items-center gap-2 text-left"
                        >
                          <ChevronDown
                            className={cn(
                              'h-4 w-4 text-gray-500 transition-transform',
                              collapsed && '-rotate-90'
                            )}
                            aria-hidden
                          />
                          <span className="text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300">
                            {category.replace(/_/g, ' ')}
                          </span>
                          <span className="rounded-full bg-gray-200 px-2 py-0.5 text-[10px] font-semibold text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                            {categorySelected}/{perms.length}
                          </span>
                        </button>
                        <button
                          type="button"
                          onClick={() => selectAllInCategory(perms)}
                          className="text-xs font-medium text-primary hover:underline"
                        >
                          {allSelected ? 'Clear' : 'Select all'}
                        </button>
                      </div>

                      {!collapsed && (
                        <ul className="divide-y divide-gray-100 dark:divide-gray-700/60">
                          {perms.map((permission) => {
                            const checked = selectedPermissionIds.includes(permission._id)
                            return (
                              <li key={permission._id}>
                                <label
                                  className={cn(
                                    'flex cursor-pointer items-start gap-3 border-l-2 px-4 py-2.5 transition-colors',
                                    checked
                                      ? 'border-primary bg-primary/5 dark:bg-primary/10'
                                      : 'border-transparent hover:bg-gray-50 dark:hover:bg-gray-800/60'
                                  )}
                                >
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => togglePermission(permission._id)}
                                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary dark:border-gray-600 dark:bg-gray-700"
                                  />
                                  <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2">
                                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                        {permissionLabel(permission)}
                                      </span>
                                      <span className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-[10px] text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                                        {permission.module}:{permission.action}
                                      </span>
                                    </div>
                                    {permission.description && (
                                      <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                                        {permission.description}
                                      </p>
                                    )}
                                  </div>
                                </label>
                              </li>
                            )
                          })}
                        </ul>
                      )}
                    </div>
                  )
                })
              )}
            </div>
          </Card>
        </div>

        {/* RIGHT COLUMN — sticky summary */}
        <aside className="hidden lg:col-span-1 lg:block">
          <div className="sticky top-4 space-y-4">
            <Card
              header={
                <div className="flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <Sparkles className="h-5 w-5" aria-hidden />
                  </span>
                  <div>
                    <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      Summary
                    </h2>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Live preview of this role.
                    </p>
                  </div>
                </div>
              }
              contentClassName="p-5 space-y-4"
            >
              <div>
                <p className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Name
                </p>
                <p className="mt-0.5 truncate text-base font-semibold text-gray-900 dark:text-gray-100">
                  {name?.trim() || 'Untitled role'}
                </p>
                {slug?.trim() && (
                  <code className="mt-1 inline-block rounded bg-gray-100 px-1.5 py-0.5 font-mono text-[11px] text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                    {slug}
                  </code>
                )}
              </div>

              <div className="flex flex-wrap gap-2">
                <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-700 dark:bg-gray-800 dark:text-gray-300">
                  <Layers className="h-3 w-3" aria-hidden />
                  {scopeBadgeText(effectiveScope)}
                </span>
                {activeTemplate && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary">
                    {activeTemplate.name} template
                  </span>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <p className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Permissions
                  </p>
                  <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                    {selectedCount}/{totalPermissions}
                  </span>
                </div>
                <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
                  <div
                    className="h-full rounded-full bg-primary transition-all"
                    style={{
                      width: totalPermissions
                        ? `${(selectedCount / totalPermissions) * 100}%`
                        : '0%',
                    }}
                  />
                </div>
                {selectedByCategory.size > 0 && (
                  <ul className="mt-3 space-y-1.5">
                    {Array.from(selectedByCategory.entries()).map(([cat, count]) => (
                      <li
                        key={cat}
                        className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400"
                      >
                        <span className="flex items-center gap-1.5">
                          <CheckCircle2 className="h-3 w-3 text-primary" aria-hidden />
                          {cat.replace(/_/g, ' ')}
                        </span>
                        <span className="font-medium text-gray-700 dark:text-gray-300">
                          {count}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="space-y-2 border-t border-gray-200 pt-4 dark:border-gray-700">
                <Button
                  form={formId}
                  type="submit"
                  variant="primary"
                  disabled={!selectedTemplate} loading={saving}
                  className="w-full justify-center"
                  title={!selectedTemplate ? 'Pick a template first' : undefined}
                >
                  {saving ? 'Creating…' : 'Create role'}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => navigate('/roles')}
                  className="w-full justify-center"
                >
                  Cancel
                </Button>
                {!selectedTemplate && (
                  <p className="text-center text-xs text-gray-500 dark:text-gray-400">
                    Pick a template above to enable Create.
                  </p>
                )}
              </div>
            </Card>
          </div>
        </aside>
      </form>

      {/* Mobile sticky action bar */}
      <div className="fixed inset-x-0 bottom-0 z-30 border-t border-gray-200 bg-white/90 backdrop-blur dark:border-gray-700 dark:bg-gray-900/90 lg:hidden">
        <div className="flex items-center justify-between gap-3 px-4 py-3">
          <div className="text-xs text-gray-600 dark:text-gray-400">
            <span className="font-semibold text-gray-900 dark:text-gray-100">
              {selectedCount}
            </span>{' '}
            / {totalPermissions} permissions
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={() => navigate('/roles')}>
              Cancel
            </Button>
            <Button
              form={formId}
              type="submit"
              variant="primary"
              size="sm"
              disabled={!selectedTemplate} loading={saving}
              title={!selectedTemplate ? 'Pick a template first' : undefined}
            >
              {saving ? 'Creating…' : 'Create role'}
            </Button>
          </div>
        </div>
      </div>
    </PageShell>
  )
}

CreateRolePage.displayName = 'CreateRolePage'
